import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getCurrentUserProfile, updateUserProfile as updateProfileAPI } from '../lib/api';
import { buildVoiceProfileUpdates, shouldSyncVoiceProfile } from '../lib/api/vapi';
import { hasActiveSubscription } from '../lib/utils/subscriptionAccess';
import { applyLocale, getStoredLocale, normalizeLocale } from '../i18n';

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const voiceSyncAttempted = useRef(false);
  const profileLoadedForUserRef = useRef(null);

  const profilePendingForUser = Boolean(userId) && profileLoadedForUserRef.current !== userId;
  if (profilePendingForUser && !loading) {
    setLoading(true);
  }

  const fetchProfile = useCallback(async (options = {}) => {
    const { silent } = options;
    if (!userId) {
      setProfile(null);
      setLoading(false);
      profileLoadedForUserRef.current = null;
      return;
    }

    // Default: keep UI when profile for this user is already loaded.
    // Override with { silent: false } if a full loading state is needed.
    const hasProfileForUser = profileLoadedForUserRef.current === userId;
    const useSilent = silent ?? hasProfileForUser;

    try {
      if (!useSilent) setLoading(true);
      const data = await getCurrentUserProfile();
      setProfile(data);
      profileLoadedForUserRef.current = userId;
      setError(null);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      if (!useSilent) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    voiceSyncAttempted.current = false;
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Persist UI language: profile preference wins, otherwise last local choice / English.
  useEffect(() => {
    if (!userId) return;
    if (!profile) return;
    const locale = normalizeLocale(profile.preferred_locale || getStoredLocale());
    applyLocale(locale);
  }, [userId, profile?.preferred_locale]);

  useEffect(() => {
    if (!userId || !profile || loading || voiceSyncAttempted.current) return;
    if (!hasActiveSubscription(profile)) return;
    if (!shouldSyncVoiceProfile(profile)) return;
    if (!import.meta.env.VITE_VAPI_API_KEY) return;

    voiceSyncAttempted.current = true;

    (async () => {
      try {
        const updates = await buildVoiceProfileUpdates(profile);
        if (!updates) return;

        const updated = await updateProfileAPI(userId, updates);
        setProfile(updated);
      } catch (err) {
        console.error('Failed to sync default voice profile:', err);
        voiceSyncAttempted.current = false;
      }
    })();
  }, [userId, profile, loading]);

  const updateProfile = async (updates) => {
    if (!userId) return { data: null, error: 'No user' };

    try {
      const updated = await updateProfileAPI(userId, updates);
      setProfile(updated);
      return { data: updated, error: null };
    } catch (err) {
      console.error('Error updating profile:', err);
      return { data: null, error: err.message };
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profile,
        loading,
        error,
        updateProfile,
        refetch: fetchProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
